import React, { memo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useSidebarStore } from 'store/sidebar.store';
import { useIsMobile } from 'hooks/use-media-query';
import { LeftSidebarContent } from './LeftSidebarContent';
import { LeftSidebarRail } from './LeftSidebarRail';
import Logo from './Logo';

export const LeftSidebar = memo(() => {
    const { isOpen, settings, toggleOpen, focusMode } = useSidebarStore();
    const location = useLocation();
    const pathname = location.pathname;
    const { t } = useTranslation();
    // MainLayout only mounts this component at >=768px (below that it renders
    // the dedicated phone shell instead — see layouts/Main/Main.tsx), so in
    // practice this is always false today. Kept as a real check (not assumed)
    // so the off-canvas drawer below stays correct dialog semantics if this
    // component is ever reused at a narrower width.
    const isDrawerMode = useIsMobile();

    // Drawer-only affordances: Escape closes it and returns focus to the
    // element that opened it (the sidebar's own toggle controls, which live in
    // LeftSidebarContent/LeftSidebarRail, own that focus return via native
    // button focus already, so we just need to close on Escape here).
    useEffect(() => {
      if (!isOpen || !isDrawerMode) return;
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') toggleOpen();
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, isDrawerMode, toggleOpen]);

    // Hide sidebar on auth pages
    const authPaths = ['/login', '/signup', '/forgot-password', '/reset-password', '/welcome'];
    if (authPaths.some((path) => pathname?.includes(path))) {
        return (<div className="invisible lg:visible fixed top-0 left-0 z-20 pt-6 pl-6">
        <Logo width={20} height={20}/>
      </div>);
    }
    return (<>
      {/* Mobile overlay — rendered BEFORE the sidebar so sidebar sits on top.
          Suppressed in focus mode: the expanded nav is force-hidden there too,
          so a backdrop with nothing to dim behind it would just block clicks. */}
      {isOpen && !focusMode && (<div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 md:hidden" onClick={toggleOpen}/>)}

      {/* Expanded light nav (228px). Below md (768px): off-canvas drawer that
          slides in on open. At md+ (768px, matching useIsMobile's threshold —
          see Main.tsx): shown when open; hidden (the navy rail takes over) when
          collapsed. Force-hidden in focus mode regardless of the persisted
          open state, so a focused workspace always reclaims the full width.
          height: h-screen (100vh) is a fallback; the inline 100dvh tracks the
          actually-visible viewport on mobile (iOS counts the toolbar area in
          100vh, which pushed the footer UserMenu off-screen). */}
      <aside
        data-sidebar="expanded"
        data-state={isOpen ? 'pinned' : 'collapsed'}
        style={{ top: 'var(--banner-h, 0px)', height: 'calc(100dvh - var(--banner-h, 0px))' }}
        className={cn('fixed left-0 w-[248px] overflow-hidden',
        'transition-transform ease-in-out duration-300',
        isOpen ? 'translate-x-0' : '-translate-x-full',
        // Desktop collapsed → hide the expanded nav; the rail below is shown.
        !isOpen && 'md:hidden',
        // Focus mode always wins, even if isOpen was persisted true.
        focusMode && 'hidden',
        // z-50 below md (drawer overlay), z-20 at md+ (inline desktop sidebar)
        'z-50 md:z-20', settings.disabled && 'hidden')}
        // Only a real overlay dialog when acting as the narrow-viewport drawer;
        // at md+ this is persistent inline chrome, not a modal.
        role={isDrawerMode && isOpen ? 'dialog' : undefined}
        aria-modal={isDrawerMode && isOpen ? true : undefined}
        aria-label={t('nav.sidebar_label', 'Navigation')}
        // When closed off-canvas (drawer mode), remove the content — and its
        // focusable descendants — from the tab order / a11y tree so keyboard
        // users can't tab into nav links that are transformed out of view.
        // `inert` isn't in this project's @types/react yet, so apply it via a
        // loosely-typed spread rather than a typed JSX attribute.
        {...(!isOpen ? ({ inert: true } as Record<string, unknown>) : {})}
      >
        <div className="bg-background relative overflow-hidden h-full flex flex-col w-[248px]">
          {/* Content — the org switcher (top) and user block (bottom) live inside
              LeftSidebarContent, matching the redesign shell. */}
          <div className="flex-1 min-h-0 overflow-x-hidden py-3">
            <LeftSidebarContent />
          </div>
        </div>
      </aside>

      {/* Collapsed navy icon rail — desktop only (md+, 768px), shown when the
          nav is collapsed (design 1b). Suppressed in focus mode (focused
          workspaces reclaim the full width) and below md (the header hamburger
          opens the drawer). */}
      <aside
        data-sidebar="rail"
        style={{ top: 'var(--banner-h, 0px)', height: 'calc(100dvh - var(--banner-h, 0px))' }}
        className={cn(
          'group/rail fixed left-0 z-20 hidden w-[68px] overflow-hidden',
          !isOpen && !focusMode && 'md:block',
          settings.disabled && 'md:hidden'
        )}
      >
        <LeftSidebarRail />
      </aside>
    </>);
});
LeftSidebar.displayName = 'LeftSidebar';
export default LeftSidebar;
